// Only the styles every route needs ship in the entry bundle, and only the ones the SDK does
// not already carry.
//
// `@mountaya/studio-react/styles.css` is the SDK's one sheet: Mantine's rules wrapped in
// `@layer mantine` first, then the rendering family (MapLibre, the elements, the studio)
// unlayered. It carries the design system, so the entry is the only place it can be imported
// from: the notifications below and the root route's pending spinner render Mantine outside any
// route, and the auth, onboarding and share surfaces mount outside the app shell. A stylesheet
// imported from a route's own subtree reaches none of them, and its absence shows only on a cold
// load, because a client-side navigation arrives with whatever the previous route already put in
// the document. The map renderer's rules ride along on the login form as the price of one sheet.
//
// It is imported last, after this application's own sheets, so the family's unlayered rules keep
// the place they held when they arrived with the map chunk, and its layered Mantine half loses to
// every unlayered rule `packages/theme` or `./styles.css` declares whatever the order. It is
// imported nowhere else, so the bytes ship once and no unlayered copy exists to invert that.
// `entryStylesheets.test.ts` holds both halves.
//
// `@mantine/notifications` is THIS application's surface and not the studio's, so nothing the
// SDK publishes carries it. The charts stylesheet lives beside its sole consumer
// (`iam/organization/UsageCharts.tsx`) for the same reason, on the lazy chunk that renders it.
import "@mantine/notifications/styles.css";
import "@mountayaapp/theme/fonts.css";
import "@mountayaapp/theme/reducedMotion.css";
import "./styles.css";
import "@mountaya/studio-react/styles.css";

import { Center, Loader } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import {
  createQueryClient,
  MountayaClientProvider,
  setRequestOrganizationSource,
} from "@mountayaapp/api";
import { createIdentityStore } from "@mountayaapp/identity";
import { IdentityProvider } from "@mountayaapp/theme";

import { startAnalytics } from "./analytics/rules/start";
import { BrandedThemeProvider } from "./BrandedThemeProvider";
import { env } from "./env";
import { LocaleProvider } from "./locale/providers/LocaleProvider";
import { routeTree } from "./routeTree.gen";
import { RouteError } from "./shared/components/RouteError";
import { RouteNotFound } from "./shared/components/RouteNotFound";
import { openOrganizationOf } from "./shared/rules/openOrganization";
import { scheduleStudioWarm } from "./warmStudio";

/**
 * identityStore is the render-free identity store, created once at module scope
 * so the router context (read by beforeLoad guards, outside React) and the
 * IdentityProvider (read by the React tree) share a single instance. Imported
 * from the @mountayaapp/identity barrel, not a deep store path, per the
 * web-src-no-direct-core-internals architecture rule.
 */
const identityStore = createIdentityStore();

/**
 * queryClient holds REST server state (TanStack Query). GraphQL server state
 * lives in a separate urql client; neither lives in Zustand. Hoisted to module
 * scope so the _app guard can prefetch the org list via the router context.
 */
const queryClient = createQueryClient();

/**
 * RoutePending is the fallback shown while a route resolves: its lazy component
 * chunk downloads and its beforeLoad runs. A centered full-height spinner that
 * mirrors the root pending state so a navigation into a not-yet-loaded chunk
 * reads the same as the initial session resolve.
 */
function RoutePending() {
  return (
    <Center mih="100vh">
      <Loader />
    </Center>
  );
}

/**
 * router is the typed TanStack Router instance built from the generated route
 * tree. Its context carries the module-scoped identity store + query client so
 * beforeLoad can resolve the session, prefetch, and guard before render.
 * defaultPreload "intent" warms a route's chunk + loader on link hover/focus so
 * the navigation feels instant; the pending fallback only appears when a route
 * is still resolving after defaultPendingMs, and once shown it stays for at
 * least defaultPendingMinMs to avoid a flash.
 */
const router = createRouter({
  context: { identityStore, queryClient },
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: RouteNotFound,
  defaultPendingComponent: RoutePending,
  defaultPendingMinMs: 200,
  defaultPendingMs: 300,
  defaultPreload: "intent",
  routeTree,
});

// The organization the console has open, read off the router as each Data API and Tile API
// request leaves, so the API records the studio's usage under the organization in view. A source
// rather than a value pushed from an effect: effects run children first, and the studio's map
// would draw its first tiles before a parent's effect had named anything.
setRequestOrganizationSource(() => openOrganizationOf(router.state));

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("missing #root element");
}

// Analytics starts at bootstrap, ahead of the first render, the same way the
// documentation site does. It is deliberately NOT deferred to an idle callback:
// session replay records only from the moment the recorder starts and PostHog
// captures its pageview at init, so any delay silently discards the opening of the
// session, and on a page as busy as the studio an idle callback may never run at
// all. Deferring buys nothing either, because the SDK is a dynamic import inside
// initAnalytics and so already lives in its own chunk rather than the entry
// bundle. The call is fire-and-forget; nothing here blocks render. startAnalytics
// owns the enablement gate, so the embed surface, the e2e run, and any build
// without a project token never reach the network.
startAnalytics();

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* The application's credential, in one place. Everything under here reads the
          signed-in Ory session cookie; the embed and share surfaces nest their own
          provider so their subtrees can never fall back to it. */}
      <MountayaClientProvider
        baseUrl={env.apiBaseUrl}
        dataUrl={env.dataApiUrl}
        tilesUrl={env.tileApiUrl}
      >
        <IdentityProvider store={identityStore}>
          {/* BrandedThemeProvider reads the active org from the identity store and tints
            Mantine's primary scale with that org's brand palette, re-theming on an org
            switch; an individual user (no org) keeps the default blue. It renders the
            shared ThemeProvider/MantineProvider underneath, so Notifications and the
            router below still sit inside a MantineProvider. */}
          <BrandedThemeProvider>
            {/* Notifications park bottom-right, lifted above the studio's MapLibre
              attribution + map controls that sit in the same corner, via a
              classNames hook + the scoped CSS in styles.css. A plain `style` prop
              would spread the offset onto every position container (Mantine renders
              one per corner), so the hook scopes it to just the bottom-right stack. */}
            <Notifications
              classNames={{ root: "mountaya-notifications-bottom-offset" }}
              position="bottom-right"
            />
            <LocaleProvider>
              <RouterProvider router={router} />
            </LocaleProvider>
          </BrandedThemeProvider>
        </IdentityProvider>
      </MountayaClientProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Warm the studio chunk — the heaviest one, carrying the map and MapLibre — once the
// browser is idle, so a user who lands on login or an IAM page has it ready by the time
// they navigate in. scheduleStudioWarm owns the guards: it skips the public embed and
// share surfaces entirely, and skips a preload that would race a still-resolving match.
// The typed `to` stays here, where the router registration above applies. Work that must
// happen (analytics, above) is called outright instead.
scheduleStudioWarm({
  pathname: window.location.pathname,
  preload: () => router.preloadRoute({ to: "/studio" }),
  status: () => router.state.status,
});
